/**
 * Which category goes across the top, which goes down the side, and where the
 * blocks sit — for print and for screen, from one definition.
 *
 * A logic grid is a staircase of blocks, one per pair of categories, and the
 * arrangement is a choice: any pairing can be drawn with either category on
 * either axis. The choice only has to be *consistent*, and it was not. The
 * interior put Baker and Flavor across the top; the site put them across the
 * top in the opposite order and mirrored the staircase with them. A reader
 * solving on screen with the book open was reading two different pictures of
 * the same puzzle.
 *
 * So this lives outside `pdf/`, where the site can reach it: nothing imported
 * through the site's `@gen` alias may pull in pdf-lib, and `pdf/grid.ts` does.
 */

/**
 * Categories across the top and down the side, in printed order.
 *
 * The first category is the spine: it runs down the side against every other,
 * and never appears across the top. The rest go across the top in order, and
 * back down the side in reverse — which is what makes the staircase step in
 * from the left rather than out to the right.
 */
export function gridAxes(categoryCount: number): { rowCats: number[]; colCats: number[] } {
  const colCats = Array.from({ length: categoryCount - 1 }, (_, i) => i + 1);
  const rowCats = [
    0,
    ...Array.from({ length: categoryCount - 2 }, (_, i) => categoryCount - 1 - i),
  ];
  return { rowCats, colCats };
}

/** Whether a block is drawn where a row category meets a column category. */
export function hasBlock(rowCat: number, colCat: number): boolean {
  return rowCat === 0 || colCat < rowCat;
}

/**
 * Whether a pair of categories is drawn with the lower one down the side.
 *
 * A cell is always *named* with its lower category first — `marks.ts` keys on
 * `i:a:j:b` with i < j so one square has exactly one name — but only blocks
 * involving the spine are *drawn* that way. Every other block is transposed:
 * Flavor x Table is named Flavor-then-Table and drawn with Table down the side.
 *
 * Anything that tells a reader to look along a row or a column has to ask this
 * first, or it sends them down the wrong line.
 */
export function drawnUpright(rowCat: number, colCat: number): boolean {
  return hasBlock(rowCat, colCat);
}

/**
 * Every block, in the order a reader's eye crosses them: along the top row,
 * then each row of the staircase below it, left to right.
 *
 * Not the same as enumerating category pairs in index order, which is what
 * `marks.ts` does for naming. That order runs down the *first column* of the
 * staircase from the bottom after finishing the top row, and stepping through
 * blocks one at a time in that sequence reads as jumping about at random,
 * because it bears no relation to where the blocks sit on the page.
 *
 * Pairs come back lower category first, so they can be used as cell names
 * directly without the caller having to remember to canonicalise them.
 */
export function drawnBlocks(categoryCount: number): Array<[number, number]> {
  const { rowCats, colCats } = gridAxes(categoryCount);
  const out: Array<[number, number]> = [];
  for (const rowCat of rowCats) {
    for (const colCat of colCats) {
      if (!hasBlock(rowCat, colCat)) continue;
      out.push(rowCat < colCat ? [rowCat, colCat] : [colCat, rowCat]);
    }
  }
  return out;
}
